import React, { useEffect, useMemo, useCallback } from 'react';
import { SelectOption } from 'global';
import { FormProvider, useForm } from 'react-hook-form';
import { FiltersDrover, BaseUIProps, FilterEvent } from 'components/shared/Filters';
import { FinanceFilters, FinanceFilterType } from './types';
import { initialFinanceFiltersValue } from './constants';
import { financeComponentCardToTypeMapper } from './componentMapper';

interface Props extends BaseUIProps {
  configuration: FinanceFilterType[];
  externalFiltersValue?: Partial<FinanceFilters> | null;
  externalMeta?: Partial<Record<FinanceFilterType, SelectOption[]>>;
  onChanged: (filters: Partial<FinanceFilters>, event: FilterEvent) => void;
}

export const FinanceFiltersContainer = ({
  onClose,
  onChanged,
  configuration,
  externalMeta = {},
  externalFiltersValue,
  ...props
}: Props) => {
  const defaultFiltersValues = useMemo(() => {
    return configuration.reduce<Partial<FinanceFilters>>((acc, type) => {
      if (initialFinanceFiltersValue[type] !== undefined) {
        acc[type] = initialFinanceFiltersValue[type] as any;
      }
      return acc;
    }, {});
  }, [configuration]);

  const form = useForm<Partial<FinanceFilters>>({
    defaultValues: externalFiltersValue ?? defaultFiltersValues,
  });

  useEffect(() => {
    if (externalFiltersValue) {
      form.reset({
        ...defaultFiltersValues,
        ...externalFiltersValue,
      });
    }
    // Only reset when externalFiltersValue actually changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalFiltersValue]);

  const handleApplyFilters = useCallback(async () => {
    const currentValues = form.getValues();
    onClose();
    onChanged(currentValues, { type: 'apply' });
  }, [onClose, onChanged, form]);

  const handleResetFilters = useCallback(async () => {
    form.reset(defaultFiltersValues);
    onClose();
    onChanged(defaultFiltersValues, { type: 'reset' });
  }, [form, defaultFiltersValues, onClose, onChanged]);

  return (
    <FormProvider {...form}>
      <FiltersDrover<FinanceFilterType>
        onClose={onClose}
        externalMeta={externalMeta}
        configuration={configuration}
        componentMap={financeComponentCardToTypeMapper}
        actions={{ onReset: handleResetFilters, onApply: handleApplyFilters }}
        {...props}
      />
    </FormProvider>
  );
};
